<script setup lang="ts">
import TokenService from '@/services/token.service'
import type { AlertSocketMessageModel } from '@/types/socket/SocketAlertModel'
import { io, type Socket } from 'socket.io-client'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { SOCKET_RETRY_DELAY } from './socketUtils'

const DEVICE_URL = `${import.meta.env.VITE_SOCKET_URL ?? window.location.origin}/device-config`

type Props = {
    device_ids?: number
}
const props = defineProps<Props>()
type Emits = {
    (e: 'update', value: AlertSocketMessageModel): void
}
const emit = defineEmits<Emits>()

const store = useStore()

const socketState = ref<Props>()

const connected = ref(true)

const getNewAccessToken = () => store?.state?.authentication?.accessToken ?? ''

const socketAlert: Socket = io(DEVICE_URL, {
    autoConnect: false,
    retries: 5,
    path: '/ws/device-config',
    transports: ['websocket'],
    query: {
        device_ids: socketState.value?.device_ids,
        token: TokenService.getLocalAccessToken()
    }
})
    .on('connect', () => (connected.value = socketAlert.connected))
    .on('disconnect', () => (connected.value = socketAlert.connected))
    .on('connect_error', res => {
        if (res?.message === 'Authentication error') {
            connected.value = false
            socketAlert.io.opts.query = {
                ...socketAlert.io.opts.query,
                token: getNewAccessToken()
            }
            setTimeout(() => socketAlert.disconnect().connect(), SOCKET_RETRY_DELAY)
        }
    })
    .on('message', (res: AlertSocketMessageModel) => emit('update', res))

const setSocket = (device_ids?: number) => {
    if (!device_ids || device_ids === socketState.value?.device_ids) return
    updateSocketState('device_ids', props.device_ids)
    reloadSocket()
}

const reloadSocket = () => {
    if (socketAlert.connected) socketAlert.disconnect().connect()
    else socketAlert.connect()
}

const updateSocketState = (key: string, value?: number) => {
    socketState.value = { ...socketState.value, [key]: value }
    socketAlert.io.opts.query = { ...socketAlert.io.opts.query, [key]: value }
}

onMounted(() => setSocket(props.device_ids))
watch(
    () => props.device_ids,
    newValue => setSocket(newValue),
    { immediate: true }
)

onBeforeUnmount(() => {
    socketAlert.disconnect()
})
</script>
<template><slot /></template>
